import mqtt from 'mqtt'
import store from '@/store'
import session from './session'

const service = {
  client: null, // mqtt连接 对象
  connectionId: Math.random().toString(36).slice(2, 12),
  lastClientId: null, // 最近一次连接使用的客户端ID
  reconnectTimer: null,
  reconnectRequest: null,
  connecting: false,
  paused: false, // 页面隐藏或App后台时暂停连接和自动重连
  persistentSubscriptions: Object.create(null), // 页面切换期间仍保持的主题订阅及最后一条消息
  url: store.state.settings.mqttHost, // mqtt服务器地址
  options: {
    port: store.state.settings.mqttPort,
    connectTimeout: 4000,
    clientId: '',
    clean: true,
    keepalive: 30,
    queueQoSZero: false,
    reconnectPeriod: 0 // Reconnect only after server-side session validation.
  },
  mqttConf,
  buildClientId,
  isConnected,
  reconnectNow,
  mqttPause,
  mqttResume,
  mqttDestroy,
  doSubscribe,
  doPersistentSubscribe,
  publishFun,
  publishCommand,
  publishDelivery
}

export default service

session.onClear = () => closeClient.call(service)

/**
 * @Description: mqtt对象初始化
 */
function mqttConf () {
  if (this.paused || !session.credentials || !session.isValid(session.token) || session.token !== store.state.user.token) return null

  const clientId = this.buildClientId()

  if (!clientId) {
    store.dispatch('fetchLog', { content: 'MQTT客户端ID生成失败，请重新登录', sourceType: 'system' })
    return null
  }

  // 账号切换后必须断开旧连接，避免继续沿用上一个账号的 clientId。
  if (this.client && this.lastClientId && this.lastClientId !== clientId) {
    closeClient.call(this)
  }

  this.options.clientId = clientId
  this.lastClientId = clientId

  if (!this.client) {
    // Reuse subscriptions, but revalidate the login before reconnecting.
    this.connecting = true
    this.client = mqtt.connect(this.url, { ...this.options, ...session.credentials })

    const client = this.client
    this.client.on('connect', () => {
      if (client !== this.client) return
      this.connecting = false
      if (this.paused || !session.isValid(session.token) || session.token !== store.state.user.token) this.mqttPause()
    })
    this.client.on('error', () => {
      if (client !== this.client || this.paused) return
      store.state.app.mqttState = false
      store.dispatch('fetchLog', { content: '指令服务器异常中断', sourceType: 'system' })
    })
    this.client.on('reconnect', () => {
      if (client !== this.client || this.paused) return
      store.dispatch('fetchLog', { content: '重新连接指令服务器', sourceType: 'system' })
    })
    this.client.on('offline', () => {
      if (client !== this.client || this.paused) return
      store.dispatch('fetchLog', { content: '指令服务器异常中断', sourceType: 'system' })
      store.state.app.mqttState = false
    })
    this.client.on('close', () => {
      if (client !== this.client) return
      this.connecting = false
      store.state.app.mqttState = false
      scheduleReconnect.call(this)
    })
  }

  return this.client
}
/**
 * @description: 根据当前登录账号生成 MQTT 客户端ID
 */
function buildClientId () {
  const userId = store.state.user.id || ''
  const userName = store.state.user.name || ''
  const identity = userId || userName

  if (!identity) return ''

  return `terminal_${identity}_${store.state.user.login_jti || session.epoch}_${this.connectionId}`
}

/**
 * @description: 判断 MQTT 客户端是否真正处于连接状态
 */
function isConnected () {
  return Boolean(!this.paused && session.credentials && session.isValid(session.token) && session.token === store.state.user.token && this.client && this.client.connected)
}

/**
 * @description: 校验服务器登录状态后触发重连，保留原客户端订阅。
 */
function reconnectNow (validated = false) {
  if (this.paused || !session.credentials || !session.isValid(session.token) || session.token !== store.state.user.token) return false
  if (this.isConnected()) {
    store.state.app.mqttState = true
    return true
  }
  if (this.connecting || this.reconnectRequest) return true
  clearTimeout(this.reconnectTimer)
  this.reconnectTimer = null
  const connect = () => {
    if (!this.client) return Boolean(this.mqttConf())
    if (typeof this.client.reconnect !== 'function') return false
    Object.assign(this.client.options, session.credentials, { reconnectPeriod: 0 })
    try {
      this.connecting = true
      this.client.reconnect()
      return true
    } catch (error) {
      this.connecting = false
      return false
    }
  }
  if (validated) return connect()

  const request = { token: session.token, epoch: session.epoch, validationEpoch: session.validationEpoch }
  this.reconnectRequest = request
  const current = () => this.reconnectRequest === request && !this.paused && request.token === store.state.user.token &&
    request.epoch === session.epoch && request.validationEpoch === session.validationEpoch && session.isValid(request.token)
  Promise.resolve().then(() => {
    if (!current()) return false
    return store.dispatch('user/fetchMqttCredentials', { force: true, quiet: true })
  }).then(valid => {
    if (!current()) return
    if (!valid || !connect()) scheduleReconnect.call(this)
  }).catch(() => {
    if (current()) scheduleReconnect.call(this)
  }).finally(() => {
    if (this.reconnectRequest === request) this.reconnectRequest = null
  })
  return true
}

function scheduleReconnect () {
  clearTimeout(this.reconnectTimer)
  this.reconnectTimer = null
  if (this.paused || !session.isValid(session.token) || session.token !== store.state.user.token) return
  this.reconnectTimer = setTimeout(() => {
    this.reconnectTimer = null
    this.reconnectNow()
  }, 2000)
}

/**
 * @description: 获取主题消息
 * @param {string} topic 主题名称
 * @param {fun} callBack 回调 传一个对象{topic订阅的主题,msg接收到主题的数据} 在父级给获取
 */
function doSubscribe (topic, callBack) {
  if (!this.client) {
    this.mqttConf()
  }
  if (!this.client) {
    store.dispatch('fetchLog', { content: `订阅失败：MQTT未初始化（${topic}）`, sourceType: 'system' })
    return null
  }

  const client = this.client
  let active = true
  const subscribe = () => {
    if (!active || client !== this.client || !this.isConnected()) return
    if (store.state.app.mqttState === false) {
      store.dispatch('fetchLog', { content: '成功连接指令服务器', sourceType: 'system' })
    }
    store.state.app.mqttState = true // 标记mqtt链接成功 在线
    client.subscribe(topic, { qos: 2 })
  }

  // 已连接时立即订阅；以后每次自动重连成功后重新订阅。
  if (client.connected) subscribe()
  client.on('connect', subscribe)

  // 获取订阅主题的消息
  const messageHandler = (messageTopic, message) => {
    if (active && client === this.client && this.isConnected()) callBack({ topic: messageTopic, msg: message.toString() })
  }
  client.on('message', messageHandler)

  // 页面级订阅必须能够在组件销毁时移除监听，避免重复进入页面后重复处理消息。
  return () => {
    active = false
    client.removeListener('connect', subscribe)
    client.removeListener('message', messageHandler)
    if (client.connected) client.unsubscribe(topic)
  }
}

/**
 * @description: 创建连接期常驻订阅。页面离开时只移除当前回调，不退订 Broker；
 * 再次进入页面会立即回放离开期间收到的最后一条消息，避免路由切换导致任务状态丢失。
 */
function doPersistentSubscribe (topic, callBack) {
  if (!this.client) {
    this.mqttConf()
  }
  if (!this.client) {
    store.dispatch('fetchLog', { content: `订阅失败：MQTT未初始化（${topic}）`, sourceType: 'system' })
    return null
  }

  const client = this.client
  let entry = this.persistentSubscriptions[topic]
  const subscriber = { active: true, callBack }

  if (!entry) {
    entry = {
      subscribers: [],
      lastMessage: null,
      subscribed: false,
      subscribing: false,
      subscribe: null,
      connectHandler: null,
      messageHandler: null
    }
    this.persistentSubscriptions[topic] = entry

    entry.subscribe = () => {
      if (client !== this.client || !this.isConnected() || entry.subscribed || entry.subscribing) return
      entry.subscribing = true
      client.subscribe(topic, { qos: 2 }, error => {
        if (client !== this.client || !this.isConnected()) return
        entry.subscribing = false
        if (error) {
          store.dispatch('fetchLog', { content: `订阅失败：${topic}`, sourceType: 'system' })
          return
        }
        entry.subscribed = true
        store.state.app.mqttState = true
      })
    }
    entry.connectHandler = () => {
      entry.subscribed = false
      entry.subscribing = false
      entry.subscribe()
    }
    entry.messageHandler = (messageTopic, message) => {
      if (client !== this.client || !this.isConnected() || messageTopic !== topic) return
      const mqttMessage = { topic: messageTopic, msg: message.toString() }
      entry.lastMessage = mqttMessage
      entry.subscribers.slice().forEach(item => {
        if (item.active) item.callBack(mqttMessage)
      })
    }

    client.on('connect', entry.connectHandler)
    client.on('message', entry.messageHandler)
  }

  entry.subscribers.push(subscriber)
  if (entry.lastMessage && this.isConnected()) callBack(entry.lastMessage)
  if (client.connected) entry.subscribe()

  return () => {
    subscriber.active = false
    const index = entry.subscribers.indexOf(subscriber)
    if (index >= 0) entry.subscribers.splice(index, 1)
  }
}
/**
  * @description: 向指定topic发送消息
  * @param {*} topic 发送信息到的主题 不包括前缀
  * @param {*} msg 要发送的信息
  */
function publishFun (topic, msg) {
  if (!this.isConnected() || typeof topic !== 'string' || !topic || /[+#]/.test(topic)) {
    return false
  }

  // topic要保持一致
  this.client.publish(topic, msg, {
    // Aircraft commands must never enter the QoS retry store or offline queue.
    qos: topic.startsWith('cmd/') ? 0 : 2,
    retain: false
  })

  return true
}
/**
 * @description: 页面隐藏或App后台时暂停MQTT，保留监听，恢复前重新验证登录。
 */
function mqttPause () {
  session.validationEpoch += 1
  this.paused = true
  Object.values(this.persistentSubscriptions).forEach(entry => { entry.lastMessage = null })
  this.connecting = false
  this.reconnectRequest = null
  clearTimeout(this.reconnectTimer)
  this.reconnectTimer = null
  if (this.client) {
    this.client.options.reconnectPeriod = 0
    this.client._deferredReconnect = null
    this.client.end(true)
  }
  store.state.app.mqttState = false
}

/**
 * @description: 登录状态校验通过后恢复原客户端连接和订阅。
 */
function mqttResume () {
  if (!session.credentials || !session.isValid(session.token) || session.token !== store.state.user.token) return false
  this.paused = false
  if (!this.client) return Boolean(this.mqttConf())
  return this.reconnectNow(true)
}

/**
 * @Description: 销毁mqtt对象
 */
function mqttDestroy (token) {
  if (token !== undefined && token !== session.token) return
  session.clear()
}

function closeClient () {
  this.reconnectRequest = null
  this.connecting = false
  clearTimeout(this.reconnectTimer)
  this.reconnectTimer = null
  this.paused = false
  if (this.client) {
    const client = this.client
    this.client = null
    client.options.reconnectPeriod = 0
    // mqtt.js 2.x may defer resume until a pending end() completes.
    client._deferredReconnect = null
    client.end(true)
    client.removeAllListeners()
    client.on('error', () => {})
    delete client.options.username
    delete client.options.password
  }
  delete this.options.username
  delete this.options.password
  this.lastClientId = null
  this.persistentSubscriptions = Object.create(null)
  store.state.app.mqttState = false
}

function planeTopic (prefix, plane) {
  if (!plane || !/^[a-z0-9]{16}$/.test(String(plane.shop_id || '')) ||
    !/^[0-9]{6}$/.test(String(plane.plane_code || ''))) return ''
  return prefix + '/' + plane.shop_id + '/' + plane.plane_code
}

function publishCommand (plane, message) {
  return this.publishFun(planeTopic('cmd', plane), message)
}

function publishDelivery (action, plane, payload) {
  if (!['start', 'finish'].includes(action) || !planeTopic('cmd', plane)) return false
  return this.publishFun('delivery/' + action + '/' + plane.shop_id, JSON.stringify(payload))
}
